import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, X, Upload, Loader2 } from "lucide-react";
import { IntakeData } from "../../pages/Index";
import {
  BatchUploadResult,
  BatchUploadService,
  QueuedPhotoMetadata,
  UploadProgress as UploadProgressType,
} from "@/lib/batchUploadService";

interface AdditionalPhotosStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack?: () => void;
}

type LocalAdditionalPhoto = {
  id: string;
  file: File;
  previewUrl: string;
  status: "pending" | "uploading" | "uploaded" | "failed";
  uploadedUrl?: string;
  error?: string;
};

export default function AdditionalPhotosStep({
  data,
  onUpdate,
  onNext,
  onBack,
}: AdditionalPhotosStepProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<UploadProgressType | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localPhotos, setLocalPhotos] = useState<LocalAdditionalPhoto[]>([]);
  const batchUploadService = useRef<BatchUploadService | null>(null);
  const createdUrls = useRef<Set<string>>(new Set());

  const uploadedPhotos = useMemo(
    () => data.additionalPhotos || [],
    [data.additionalPhotos],
  );

  useEffect(() => {
    batchUploadService.current = new BatchUploadService((progress) => {
      setUploadProgress(progress);
    });

    return () => {
      if (batchUploadService.current) {
        batchUploadService.current.cleanup();
        batchUploadService.current = null;
      }
      // Clean up all created blob URLs on unmount
      createdUrls.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      createdUrls.current.clear();
    };
  }, []);

  // Debug: Log localPhotos changes
  useEffect(() => {
    console.log("localPhotos state updated. Count:", localPhotos.length, "Photos:", localPhotos);
  }, [localPhotos]);

  const pendingLocalPhotos = useMemo(
    () =>
      localPhotos.filter(
        (photo) => photo.status === "pending" || photo.status === "failed",
      ),
    [localPhotos],
  );

  const failedLocalCount = useMemo(
    () => localPhotos.filter((photo) => photo.status === "failed").length,
    [localPhotos],
  );

  const addLocalFiles = useCallback(
    (files: File[]) => {
      console.log("addLocalFiles called with files:", files.length);
      const timestamp = Date.now();

      setLocalPhotos((prev) => {
        console.log("Previous localPhotos count:", prev.length);
        const baseSequence = (data.additionalPhotos?.length ?? 0) + prev.length;
        const additions: LocalAdditionalPhoto[] = [];

        files.forEach((file, index) => {
          const photoId = `${timestamp}_${index}_${Math.random().toString(36).slice(2, 8)}`;
          const metadata = {
            photoPosition: `additional_photo_${baseSequence + index + 1}`,
            photoSequence: baseSequence + index + 1,
            originalFilename: file.name,
          } satisfies QueuedPhotoMetadata;

          const previewUrl =
            batchUploadService.current?.addPhoto(photoId, file, metadata) ??
            URL.createObjectURL(file);

          console.log("Created preview URL for file:", file.name, previewUrl);

          // Track the created URL for cleanup
          createdUrls.current.add(previewUrl);

          additions.push({
            id: photoId,
            file,
            previewUrl,
            status: "pending",
          });
        });

        console.log("Adding", additions.length, "new photos. Total will be:", prev.length + additions.length);
        return [...prev, ...additions];
      });
    },
    [data.additionalPhotos],
  );

  const handleFileSelect = useCallback(
    (files: FileList) => {
      console.log("handleFileSelect called with files:", files.length);
      if (files.length === 0) return;
      
      // Convert FileList to array immediately before clearing the input
      const filesArray = Array.from(files);
      console.log("Converted to array:", filesArray.length, "files");
      
      addLocalFiles(filesArray);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [addLocalFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const removeLocalPhoto = useCallback((id: string) => {
    if (batchUploadService.current) {
      batchUploadService.current.removePhoto(id);
    }

    setLocalPhotos((prev) => {
      const target = prev.find((photo) => photo.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        createdUrls.current.delete(target.previewUrl);
      }
      return prev.filter((photo) => photo.id !== id);
    });
  }, []);

  const removeUploadedPhoto = useCallback(
    (index: number) => {
      const updated = uploadedPhotos.filter((_, i) => i !== index);
      onUpdate({ additionalPhotos: updated });
    },
    [uploadedPhotos, onUpdate],
  );

  const uploadedCount = uploadedPhotos.length;
  const pendingCount = pendingLocalPhotos.length;
  const totalSelected = uploadedCount + localPhotos.length;

  const uploadPendingPhotos = useCallback(async () => {
    const pending = pendingLocalPhotos;
    if (pending.length === 0) {
      return { uploaded: [] as string[], failed: [] as string[] };
    }

    if (!batchUploadService.current) {
      console.error("Batch upload service is not available");
      const failedIds = pending.map((photo) => photo.id);
      return { uploaded: [], failed: failedIds };
    }

    if (!data.formUid) {
      console.warn("Form UID missing; unable to upload additional photos yet.");
      return { uploaded: [], failed: pending.map((photo) => photo.id) };
    }

    setIsUploading(true);

    const pendingIds = new Set(pending.map((photo) => photo.id));

    setLocalPhotos((prev) =>
      prev.map((photo) =>
        pendingIds.has(photo.id)
          ? { ...photo, status: "uploading", error: undefined }
          : photo,
      ),
    );

    try {
      const result: BatchUploadResult =
        await batchUploadService.current.uploadAllPhotos(
          data.email,
          data.formUid,
        );

      const successfulIds = new Set(Object.keys(result.uploadedPhotos));

      setLocalPhotos((prev) =>
        prev.reduce<LocalAdditionalPhoto[]>((acc, photo) => {
          if (!pendingIds.has(photo.id)) {
            acc.push(photo);
            return acc;
          }

          if (successfulIds.has(photo.id)) {
            URL.revokeObjectURL(photo.previewUrl);
            createdUrls.current.delete(photo.previewUrl);
            return acc;
          }

          if (result.failedPhotos.includes(photo.id)) {
            acc.push({
              ...photo,
              status: "failed",
              error:
                result.errors[photo.id] ?? "Upload failed. Please try again.",
            });
            return acc;
          }

          acc.push(photo);
          return acc;
        }, []),
      );

      const finalAdditionalPhotos = [...(data.additionalPhotos ?? [])];
      const finalAdditionalMediaIds = [...(data.additionalPhotoMediaIds ?? [])];

      Object.entries(result.uploadedPhotos).forEach(([photoId, url]) => {
        finalAdditionalPhotos.push(url);
        const mediaId = result.mediaIds[photoId];
        if (mediaId) {
          finalAdditionalMediaIds.push(mediaId);
        }
      });

      if (Object.keys(result.uploadedPhotos).length > 0) {
        onUpdate({
          additionalPhotos: finalAdditionalPhotos,
          additionalPhotoMediaIds: finalAdditionalMediaIds,
        });
      }

      return {
        uploaded: Object.values(result.uploadedPhotos),
        failed: result.failedPhotos,
      };
    } catch (error) {
      console.error("Failed to upload additional photos:", error);
      const failedIds = pending.map((photo) => photo.id);

      setLocalPhotos((prev) =>
        prev.map((photo) =>
          failedIds.includes(photo.id)
            ? {
                ...photo,
                status: "failed",
                error:
                  error instanceof Error
                    ? error.message
                    : "Upload failed. Please try again.",
              }
            : photo,
        ),
      );

      return { uploaded: [], failed: failedIds };
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  }, [
    batchUploadService,
    pendingLocalPhotos,
    data.email,
    data.formUid,
    data.additionalPhotos,
    data.additionalPhotoMediaIds,
    onUpdate,
  ]);

  const handleNext = useCallback(async () => {
    if (isUploading) {
      return;
    }

    const result = await uploadPendingPhotos();

    if (result.failed.length > 0) {
      alert(
        "Some additional photos failed to upload. Please retry or remove them before continuing.",
      );
      return;
    }

    onNext();
  }, [isUploading, uploadPendingPhotos, onNext]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Additional Photos
        </h2>
        <p className="text-gray-600">
          Upload any additional photos you'd like us to review (optional)
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver
            ? "border-blue-400 bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        } cursor-pointer`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => {
          console.log("Upload area clicked, triggering file input");
          fileInputRef.current?.click();
        }}
      >
        <div className="space-y-4">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <Upload className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900 mb-1">
              Upload Additional Photos
            </p>
            <p className="text-gray-600 text-sm">
              Drag and drop files here, or click to select
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Supports JPEG, PNG, WebP (max 10MB each)
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            console.log("File input onChange triggered, files:", e.target.files?.length);
            e.target.files && handleFileSelect(e.target.files);
          }}
        />
      </div>

      {isUploading && uploadProgress && uploadProgress.total > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-blue-800 text-sm font-medium">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>
              Uploading {uploadProgress.current} of {uploadProgress.total}{" "}
              photos…
            </span>
          </div>
          <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all"
              style={{
                width: `${Math.min(100, Math.round((uploadProgress.current / uploadProgress.total) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}

      {localPhotos.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
              Pending Uploads ({localPhotos.length})
            </h3>
            {failedLocalCount > 0 && (
              <span className="text-xs text-red-600">
                {failedLocalCount} upload{failedLocalCount === 1 ? "" : "s"}{" "}
                need attention
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {localPhotos.map((photo) => (
              <div key={photo.id} className="relative">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={photo.previewUrl}
                    alt="Pending additional photo"
                    className="w-full h-full object-cover"
                  />

                  {photo.status === "uploading" && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}

                  {photo.status === "failed" && (
                    <div className="absolute inset-x-0 bottom-0 bg-red-600/90 text-white text-xs px-2 py-1 text-center">
                      Upload failed
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeLocalPhoto(photo.id)}
                  disabled={isUploading}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white rounded-full p-1 shadow-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {uploadedPhotos.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wide">
              Uploaded Photos ({uploadedPhotos.length})
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {uploadedPhotos.map((photo, index) => (
              <div key={`${photo}-${index}`} className="relative">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={photo}
                    alt={`Additional photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => removeUploadedPhoto(index)}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Camera className="w-5 h-5 text-blue-600 mt-1" />
            <div>
              <p className="font-medium text-blue-900">
                {totalSelected} additional photo{totalSelected === 1 ? "" : "s"}{" "}
                selected
              </p>
              <p className="text-blue-700 text-sm">
                {uploadedCount} uploaded • {pendingCount} pending
                {failedLocalCount > 0 && ` • ${failedLocalCount} failed`}
              </p>
              <p className="text-blue-600 text-xs mt-2">
                We'll upload pending photos when you continue to the next step.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-6 gap-4">
        {onBack && (
          <Button onClick={onBack} variant="outline" className="px-6">
            Back
          </Button>
        )}
        <div className="flex-1" />
        <Button
          onClick={handleNext}
          disabled={isUploading}
          className="px-6 bg-[#166B93] hover:bg-[#125a7a]"
        >
          {isUploading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading...
            </span>
          ) : pendingCount > 0 ? (
            "Upload & Continue"
          ) : (
            "Continue"
          )}
        </Button>
      </div>
    </div>
  );
}
